/**
 * Canonical country values for employee + location forms.
 * Aliases keep US/USA/United States (and similar) matching without a countries table.
 */

const COUNTRY_ALIASES = Object.freeze({
  us: 'US',
  usa: 'US',
  'united states': 'US',
  'united states of america': 'US',
  moldova: 'Moldova',
  uk: 'UK',
  'united kingdom': 'UK',
  'great britain': 'UK',
  gb: 'UK',
  spain: 'Spain',
});

export const CANONICAL_COUNTRIES = Object.freeze(['Moldova', 'US', 'UK', 'Spain']);

export const LOCATION_SELECT_HINTS = Object.freeze({
  noCountry: 'Select a country first.',
  noLocations: 'No work sites in this country yet. Use + to create one.',
});

/**
 * @param {unknown} value
 * @returns {string} canonical display/storage value, or trimmed original, or ''
 */
export function normalizeCountry(value) {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  const mapped = COUNTRY_ALIASES[raw.toLowerCase()];
  return mapped || raw;
}

/**
 * @param {unknown} a
 * @param {unknown} b
 * @returns {boolean}
 */
export function countriesMatch(a, b) {
  const left = normalizeCountry(a);
  const right = normalizeCountry(b);
  if (!left || !right) return false;
  return left.toLowerCase() === right.toLowerCase();
}

/**
 * @param {{ country?: unknown }|null|undefined} location
 * @param {unknown} country
 * @returns {boolean}
 */
export function locationBelongsToCountry(location, country) {
  if (!normalizeCountry(country)) return false;
  const locCountry = location?.country;
  if (locCountry == null || String(locCountry).trim() === '') return false;
  return countriesMatch(locCountry, country);
}

/**
 * Same as locationBelongsToCountry, but empty location.country is allowed (legacy rows).
 * @param {{ country?: unknown }|null|undefined} location
 * @param {unknown} country
 * @returns {boolean}
 */
export function locationCompatibleWithCountry(location, country) {
  if (!normalizeCountry(country)) return false;
  const locCountry = location?.country;
  if (locCountry == null || String(locCountry).trim() === '') return true;
  return countriesMatch(locCountry, country);
}

/**
 * @param {unknown} value
 * @returns {string}
 */
export function countryOptionLabel(value) {
  const canonical = normalizeCountry(value);
  if (canonical === 'US') return 'United States';
  if (canonical === 'UK') return 'United Kingdom';
  return canonical;
}

/**
 * @param {Array<{ country?: unknown }>} [locations]
 * @param {unknown} country
 * @returns {number}
 */
export function countLocationsForCountry(locations, country) {
  const list = Array.isArray(locations) ? locations : [];
  return list.filter((row) => locationBelongsToCountry(row, country)).length;
}

/**
 * @param {Array<{ country?: unknown }>} [locations]
 * @param {unknown[]} [extra]
 * @param {{ includeCanonical?: boolean, exclude?: unknown[] }} [options]
 *   `exclude` hides unused catalog entries only. Countries still used by
 *   work sites (or passed in `extra`) stay in the list.
 * @returns {Array<{ label: string, value: string }>}
 */
export function buildCountryOptions(locations = [], extra = [], options = {}) {
  const includeCanonical = options.includeCanonical !== false;
  const exclude = Array.isArray(options.exclude) ? options.exclude : [];
  const seen = new Map();
  const add = (value) => {
    const canonical = normalizeCountry(value);
    if (!canonical) return;
    const key = canonical.toLowerCase();
    if (!seen.has(key)) seen.set(key, canonical);
  };

  if (includeCanonical) {
    CANONICAL_COUNTRIES.forEach(add);
  }
  exclude.forEach((value) => {
    const key = normalizeCountry(value).toLowerCase();
    if (key) seen.delete(key);
  });
  (Array.isArray(locations) ? locations : []).forEach((row) => add(row?.country));
  (Array.isArray(extra) ? extra : []).forEach(add);

  return Array.from(seen.values())
    .sort((a, b) => countryOptionLabel(a).localeCompare(countryOptionLabel(b)))
    .map((value) => ({
      label: countryOptionLabel(value),
      value,
    }));
}
